/**
 * Alshallal Pastry Company HR System - Leave Admin Page
 * Phase 4: Leave Management
 * Phase 5: Permission-based UI gating
 * HR view for all leave requests and management
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { RequestFilters, nationalityLabel } from '@/components/requests/RequestFilters';
import { DeleteRequestButton } from '@/components/requests/DeleteRequestButton';
import { BalanceExceptionsPanel } from '@/components/leave/BalanceExceptions';
import { ReviewCertificatesPanel } from '@/components/leave/ReviewCertificates';
import { UnexcusedAbsenceList } from '@/components/leave/UnexcusedAbsences';
import { RequestHistory } from '@/components/requests/RequestHistory';
import { BackfillReview } from '@/components/requests/BackfillReview';
import { format } from 'date-fns';
import {
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  User,
  Building,
  Filter,
  Settings,
  Search,
  Eye,
  Paperclip,
  Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { usePermission, Permission } from '@/contexts/PermissionContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { LeaveAttachmentList } from '@/components/leave/LeaveAttachments';
import { LeaveRequestDetailsDialog } from '@/components/leave/LeaveRequestDetailsDialog';
import {
  classifyManagerAction,
  classifyHrAction,
} from '@/lib/leaveDecisionClassifier';
import { DecisionBadge } from '@/components/shared/DecisionBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_STYLES = {
  pending_manager: { bg: 'bg-amber-100 text-amber-800', icon: Clock, label: { en: 'Pending Manager', ar: 'بانتظار المدير' } },
  pending_hr: { bg: 'bg-blue-100 text-blue-800', icon: Clock, label: { en: 'Pending HR', ar: 'بانتظار HR' } },
  approved: { bg: 'bg-green-100 text-green-800', icon: CheckCircle, label: { en: 'Approved', ar: 'موافق عليه' } },
  rejected: { bg: 'bg-red-100 text-red-800', icon: XCircle, label: { en: 'Rejected', ar: 'مرفوض' } },
};

const LeaveAdminPage = () => {
  const { token, hasRole } = useAuth();
  const { isRTL, language } = useLanguage();
  const { hasPermission, hasAnyPermission } = usePermission();
  const navigate = useNavigate();

  // Phase 5: Permission-based access
  const canViewAll = hasPermission(Permission.LEAVE_VIEW_ALL);
  const canApprove = hasAnyPermission([Permission.LEAVE_APPROVE_MANAGER, Permission.LEAVE_APPROVE_HR]);
  const canManageTypes = hasPermission(Permission.LEAVE_TYPES_MANAGE);
  const canAdjustBalance = hasPermission(Permission.LEAVE_BALANCE_ADJUST);
  // Skipping the manager's stage belongs to the bypass holders only, and the
  // API refuses everyone else - so the button is not offered to them either.
  const canBypass = hasPermission(Permission.HR_BYPASS);

  // RBAC check - using permissions
  useEffect(() => {
    if (!hasRole('hr_assistant', 'hr_manager', 'hr_admin', 'super_admin')) {
      navigate('/leave');
    }
  }, [hasRole, navigate]);

  // State
  const [requests, setRequests] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    leave_type_id: '',
    year: new Date().getFullYear().toString()
  });

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Action dialog
  const [actionDialog, setActionDialog] = useState({ open: false, request: null, action: null, isOverride: false });
  // Read-only details, so a sick note can be read before deciding — and at all.
  const [detailsDialog, setDetailsDialog] = useState({ open: false, request: null });
  const [actionReason, setActionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Leave type management dialog
  const [typeDialog, setTypeDialog] = useState(false);

  // Fetch leave types
  const fetchLeaveTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/leave/types?active_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaveTypes(response.data.leave_types || []);
    } catch (err) {
      console.error('Failed to fetch leave types:', err);
    }
  }, [token]);

  // Fetch requests
  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', '15');
      
      if (filters.status) params.append('status', filters.status);
      if (filters.leave_type_id) params.append('leave_type_id', filters.leave_type_id);
      if (filters.year) params.append('year', filters.year);
      ['nationality', 'branch_id', 'event_from', 'event_to'].forEach(key => { if (filters[key]) params.append(key, filters[key]); });

      const response = await axios.get(`${API_URL}/leave/admin/requests?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setRequests(response.data.requests || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (err) {
      setError(err.response?.data?.detail?.error || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [token, page, filters]);

  useEffect(() => {
    fetchLeaveTypes();
  }, [fetchLeaveTypes]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Process HR action (normal or override)
  const handleAction = async () => {
    if (!actionDialog.request) return;
    
    // Validation: Override and reject always need reason (min 10 chars)
    if (actionDialog.isOverride && actionReason.length < 10) return;
    if (actionReason.trim().length < 10) return;

    try {
      setSubmitting(true);

      // Phase 18.2.3.1: Use HR override endpoint for pending_manager requests
      const endpoint = actionDialog.isOverride
        ? `${API_URL}/leave/requests/${actionDialog.request.id}/hr-override`
        : `${API_URL}/leave/requests/${actionDialog.request.id}/hr-action`;

      await axios.post(endpoint, {
        action: actionDialog.action,
        reason: actionReason || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setActionDialog({ open: false, request: null, action: null, isOverride: false });
      setActionReason('');
      fetchRequests();
    } catch (err) {
      setError(err.response?.data?.detail?.error || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'PP');
    } catch {
      return dateStr;
    }
  };

  const getStatusStyle = (status) => STATUS_STYLES[status] || STATUS_STYLES.pending_manager;
  const getStatusLabel = (status) => {
    const style = getStatusStyle(status);
    return language === 'ar' ? style.label.ar : style.label.en;
  };

  const clearFilters = () => {
    setFilters({ status: '', leave_type_id: '', year: new Date().getFullYear().toString() });
    setPage(1);
  };

  // Dedupe leave types
  const uniqueTypes = leaveTypes.filter((t, i, arr) => arr.findIndex(x => x.name === t.name) === i);

  return (
    <div className="space-y-6" data-testid="leave-admin-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isRTL ? 'إدارة الإجازات' : 'Leave Administration'}
          </h1>
          <p className="text-slate-600 mt-1">
            {isRTL ? 'إدارة جميع طلبات الإجازات' : 'Manage all leave requests'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setTypeDialog(true)}>
            <Settings className="h-4 w-4 me-2" />
            {isRTL ? 'أنواع الإجازات' : 'Leave Types'}
          </Button>
          <Button variant="outline" onClick={fetchRequests} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4 me-2", loading && "animate-spin")} />
            {isRTL ? 'تحديث' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ms-auto text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      <BalanceExceptionsPanel isRTL={isRTL} />
      <ReviewCertificatesPanel isRTL={isRTL} />
      <UnexcusedAbsenceList isRTL={isRTL} />
      <div className="flex flex-wrap gap-2"><RequestHistory scope="leave" onChange={fetchRequests} /><BackfillReview scope="leave" onApplied={fetchRequests} /></div>
      <RequestFilters scope="leave" values={filters} refreshKey={total} onChange={value => { setFilters(value); setPage(1); }} />
      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            {isRTL ? 'الفلاتر' : 'Filters'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-xs">{isRTL ? 'الحالة' : 'Status'}</Label>
              <Select
                value={filters.status}
                onValueChange={(v) => { setFilters(prev => ({ ...prev, status: v === 'all' ? '' : v })); setPage(1); }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={isRTL ? 'الكل' : 'All'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'الكل' : 'All'}</SelectItem>
                  <SelectItem value="pending_manager">{isRTL ? 'بانتظار المدير' : 'Pending Manager'}</SelectItem>
                  <SelectItem value="pending_hr">{isRTL ? 'بانتظار HR' : 'Pending HR'}</SelectItem>
                  <SelectItem value="approved">{isRTL ? 'موافق عليه' : 'Approved'}</SelectItem>
                  <SelectItem value="rejected">{isRTL ? 'مرفوض' : 'Rejected'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">{isRTL ? 'نوع الإجازة' : 'Leave Type'}</Label>
              <Select
                value={filters.leave_type_id}
                onValueChange={(v) => { setFilters(prev => ({ ...prev, leave_type_id: v === 'all' ? '' : v })); setPage(1); }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={isRTL ? 'الكل' : 'All'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'الكل' : 'All'}</SelectItem>
                  {uniqueTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      {language === 'ar' && type.name_ar ? type.name_ar : type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">{isRTL ? 'السنة' : 'Year'}</Label>
              <Select
                value={filters.year}
                onValueChange={(v) => { setFilters(prev => ({ ...prev, year: v })); setPage(1); }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button variant="ghost" onClick={clearFilters}>
                {isRTL ? 'مسح الفلاتر' : 'Clear'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Requests Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : requests.length === 0 ? (
            <div className="py-12 text-center">
              <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {isRTL ? 'لا توجد طلبات' : 'No Requests Found'}
              </h3>
              <p className="text-slate-600">
                {isRTL ? 'لا توجد طلبات تطابق معايير البحث' : 'No requests match your filter criteria'}
              </p>
            </div>
          ) : (
            <>
              <TooltipProvider delayDuration={150}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{isRTL ? 'الموظف' : 'Employee'}</TableHead>
                    <TableHead>{isRTL ? 'نوع الإجازة' : 'Leave Type'}</TableHead>
                    <TableHead>{isRTL ? 'التاريخ' : 'Dates'}</TableHead>
                    <TableHead>{isRTL ? 'الأيام' : 'Days'}</TableHead>
                    <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                    <TableHead>{isRTL ? 'موافقة المدير' : 'Manager'}</TableHead>
                    <TableHead className="text-end">{isRTL ? 'إجراء HR' : 'HR Action'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((request) => {
                    const statusStyle = getStatusStyle(request.status);
                    const StatusIcon = statusStyle.icon;
                    const canAct = request.status === 'pending_hr';
                    const canOverride = request.status === 'pending_manager' && canBypass; // Phase 18.2.3.1

                    return (
                      <TableRow key={request.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              {request.employee_name?.trim() || 'Unknown'}
                              {request.attachment_count > 0 && (
                                <Badge
                                  variant="outline"
                                  className="gap-1 border-blue-200 bg-blue-50 text-blue-700 font-normal"
                                  title={isRTL ? 'يوجد مستند مرفق' : 'A supporting document is attached'}
                                  data-testid={`leave-attachment-flag-${request.id}`}
                                >
                                  <Paperclip className="h-3 w-3" />
                                  {request.attachment_count}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-500">{request.employee_department}</p>
                            <p className="text-xs text-slate-500 break-words" data-testid={`leave-snapshot-${request.id}`}>{nationalityLabel(request.filter_snapshot?.nationality)} · {request.filter_snapshot?.branch_name || 'Branch not recorded'}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {language === 'ar' && request.leave_type_name_ar ? request.leave_type_name_ar : request.leave_type_name}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p>{formatDate(request.start_date)}</p>
                            <p className="text-slate-500">→ {formatDate(request.end_date)}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{request.working_days_requested}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusStyle.bg}>
                            <StatusIcon className="h-3 w-3 me-1" />
                            {getStatusLabel(request.status)}
                          </Badge>
                          {request.rejection_reason && (
                            <p className="text-xs text-red-600 mt-1 max-w-[150px] truncate" title={request.rejection_reason}>
                              {request.rejection_reason}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <DecisionBadge
                            decision={classifyManagerAction(request)}
                            isRTL={isRTL}
                            testid={`manager-decision-${request.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDetailsDialog({ open: true, request })}
                            title={isRTL ? 'عرض التفاصيل والمرفقات' : 'View details and documents'}
                            data-testid={`leave-view-btn-${request.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <DeleteRequestButton scope="leave" row={request} onDeleted={fetchRequests} />
                          {canAct ? (
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-green-600 hover:bg-green-50"
                                onClick={() => setActionDialog({ open: true, request, action: 'approve', isOverride: false })}
                                data-testid={`hr-approve-${request.id}`}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => setActionDialog({ open: true, request, action: 'reject', isOverride: false })}
                                data-testid={`hr-reject-${request.id}`}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : canOverride ? (
                            /* Phase 18.2.3.1: HR Override buttons for pending_manager */
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-orange-600 hover:bg-orange-50 text-xs"
                                onClick={() => setActionDialog({ open: true, request, action: 'approve', isOverride: true })}
                                title={isRTL ? 'تجاوز وموافقة' : 'Override Approve'}
                                data-testid={`hr-override-approve-${request.id}`}
                              >
                                <CheckCircle className="h-4 w-4 me-1" />
                                {isRTL ? 'تجاوز' : 'Override'}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-orange-600 hover:bg-orange-50 text-xs"
                                onClick={() => setActionDialog({ open: true, request, action: 'reject', isOverride: true })}
                                title={isRTL ? 'تجاوز ورفض' : 'Override Reject'}
                                data-testid={`hr-override-reject-${request.id}`}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <DecisionBadge
                              decision={classifyHrAction(request)}
                              isRTL={isRTL}
                              testid={`hr-decision-${request.id}`}
                            />
                          )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </TooltipProvider>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-slate-600">
                    {isRTL ? `عرض ${requests.length} من ${total}` : `Showing ${requests.length} of ${total}`}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                      {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                    </Button>
                    <span className="text-sm">{page} / {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                      {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Read-only details — available at every status, including while the
          request is still with the line manager and after it is decided. */}
      <LeaveRequestDetailsDialog
        open={detailsDialog.open}
        onClose={() => setDetailsDialog({ open: false, request: null })}
        request={detailsDialog.request}
        token={token}
        isRTL={isRTL}
        language={language}
        footer={detailsDialog.request && (() => {
          const request = detailsDialog.request;
          const canAct = request.status === 'pending_hr';
          const canOverride = request.status === 'pending_manager' && canBypass;
          if (!canAct && !canOverride) return null;
          const isOverride = !canAct;
          return (
            <>
              <Button
                className={isOverride ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}
                onClick={() => {
                  setDetailsDialog({ open: false, request: null });
                  setActionDialog({ open: true, request, action: 'approve', isOverride });
                }}
                data-testid="leave-details-approve"
              >
                <CheckCircle className="h-4 w-4 me-1" />
                {isOverride
                  ? (isRTL ? 'تجاوز وموافقة' : 'Override Approve')
                  : (isRTL ? 'موافقة' : 'Approve')}
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setDetailsDialog({ open: false, request: null });
                  setActionDialog({ open: true, request, action: 'reject', isOverride });
                }}
                data-testid="leave-details-reject"
              >
                <XCircle className="h-4 w-4 me-1" />
                {isOverride
                  ? (isRTL ? 'تجاوز ورفض' : 'Override Reject')
                  : (isRTL ? 'رفض' : 'Reject')}
              </Button>
            </>
          );
        })()}
      />

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, request: null, action: null, isOverride: false })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className={actionDialog.action === 'approve' ? (actionDialog.isOverride ? 'text-orange-600' : 'text-green-600') : 'text-red-600'}>
              {actionDialog.isOverride
                ? (actionDialog.action === 'approve'
                    ? (isRTL ? 'تجاوز وموافقة على الطلب' : 'Override Approve Request')
                    : (isRTL ? 'تجاوز ورفض الطلب' : 'Override Reject Request'))
                : (actionDialog.action === 'approve'
                    ? (isRTL ? 'الموافقة على الطلب' : 'Approve Request')
                    : (isRTL ? 'رفض الطلب' : 'Reject Request'))}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {actionDialog.request && (
              <LeaveAttachmentList
                token={token}
                requestId={actionDialog.request.id}
                isRTL={isRTL}
              />
            )}

            {/* Phase 18.2.3.1: Override warning */}
            {actionDialog.isOverride && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-orange-800">
                    <p className="font-medium">
                      {isRTL ? 'تحذير: إجراء التجاوز' : 'Warning: Override Action'}
                    </p>
                    <p className="mt-1">
                      {isRTL 
                        ? 'هذا الإجراء يتجاوز موافقة المدير وسيتم تسجيله في سجل التدقيق.' 
                        : 'This action bypasses manager approval and will be audited.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Reason field - required for reject and override */}
            {(actionDialog.action === 'reject' || actionDialog.isOverride) ? (
              <div className="space-y-2">
                <Label>
                  {actionDialog.isOverride 
                    ? (isRTL ? 'سبب التجاوز (مطلوب - 10 أحرف على الأقل)' : 'Override Reason (required - at least 10 characters)')
                    : (isRTL ? 'سبب الرفض (مطلوب)' : 'Rejection Reason (required)')}
                </Label>
                <Textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder={actionDialog.isOverride 
                    ? (isRTL ? 'أدخل سبب التجاوز...' : 'Enter override reason...')
                    : (isRTL ? 'أدخل سبب الرفض...' : 'Enter rejection reason...')}
                  rows={3}
                />
                {actionReason.length > 0 && actionReason.length < 10 && (
                  <p className="text-sm text-red-500">
                    {10 - actionReason.length} {isRTL ? 'أحرف متبقية' : 'more characters needed'}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Label>{isRTL ? 'سبب الموافقة (مطلوب)' : 'Approval reason (at least 10 characters)'}</Label>
                <Textarea
                  data-testid="admin-approval-reason-input"
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder={isRTL ? 'أضف ملاحظات...' : 'Add notes...'}
                  rows={2}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, request: null, action: null, isOverride: false })}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={handleAction}
              disabled={submitting || actionReason.trim().length < 10}
              className={actionDialog.isOverride 
                ? 'bg-orange-600 hover:bg-orange-700' 
                : (actionDialog.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700')}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                actionDialog.isOverride
                  ? (actionDialog.action === 'approve' ? (isRTL ? 'تجاوز وموافقة' : 'Override Approve') : (isRTL ? 'تجاوز ورفض' : 'Override Reject'))
                  : (actionDialog.action === 'approve' ? (isRTL ? 'موافقة' : 'Approve') : (isRTL ? 'رفض' : 'Reject'))
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leave Types Dialog */}
      <Dialog open={typeDialog} onOpenChange={setTypeDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'أنواع الإجازات' : 'Leave Types'}</DialogTitle>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{isRTL ? 'النوع' : 'Type'}</TableHead>
                  <TableHead>{isRTL ? 'الأيام/سنة' : 'Days/Year'}</TableHead>
                  <TableHead>{isRTL ? 'مرفق' : 'Attach'}</TableHead>
                  <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {uniqueTypes.map(type => (
                  <TableRow key={type.id}>
                    <TableCell className="font-medium">
                      {language === 'ar' && type.name_ar ? type.name_ar : type.name}
                    </TableCell>
                    <TableCell>{type.allocation_days_per_year}</TableCell>
                    <TableCell>{type.requires_attachment ? (isRTL ? 'مطلوب' : 'Required') : '-'}</TableCell>
                    <TableCell>
                      <Badge className={type.is_active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'}>
                        {type.is_active ? (isRTL ? 'نشط' : 'Active') : (isRTL ? 'غير نشط' : 'Inactive')}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTypeDialog(false)}>
              {isRTL ? 'إغلاق' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeaveAdminPage;
